import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  SupportedLanguage,
  TranslationKey,
  TranslationParams,
  translations,
  supportedLanguages
} from '../i18n/translations';

const STORAGE_KEY = 'flourish-web-language';
const DEFAULT_LANGUAGE: SupportedLanguage = 'fr';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly language$ = new BehaviorSubject<SupportedLanguage>(this.resolveInitialLanguage());

  constructor(@Inject(DOCUMENT) private readonly document: Document) {
    this.applyDocumentLanguage(this.language$.value);
  }

  get languageChanges() {
    return this.language$.asObservable();
  }

  get currentLanguage(): SupportedLanguage {
    return this.language$.value;
  }

  isSupportedLanguage(code: string): code is SupportedLanguage {
    return supportedLanguages.includes(code as SupportedLanguage);
  }

  translate(key: TranslationKey | string, params?: TranslationParams): string {
    const dictionary = translations[this.language$.value] ?? translations.en;
    if (this.isTranslationKey(key)) {
      const fallback = translations.en[key] ?? String(key);
      const template = dictionary[key] ?? fallback;
      return this.interpolate(template, params);
    }

    return this.interpolate(String(key), params);
  }

  setLanguage(language: SupportedLanguage): void {
    const next = this.isSupportedLanguage(language) ? language : DEFAULT_LANGUAGE;
    if (next === this.language$.value) {
      this.persistLanguage(next);
      this.applyDocumentLanguage(next);
      return;
    }

    this.language$.next(next);
    this.persistLanguage(next);
    this.applyDocumentLanguage(next);
  }

  getLanguageLabel(code: string): string {
    if (code === 'fr') {
      return this.translate('language.french');
    }
    if (code === 'en') {
      return this.translate('language.english');
    }
    if (code === 'nl') {
      return this.translate('language.dutch');
    }
    return code.toUpperCase();
  }

  getLanguageLabelFull(code: string): string {
    if (code === 'fr') {
      return this.translate('language.french.full');
    }
    if (code === 'en') {
      return this.translate('language.english.full');
    }
    if (code === 'nl') {
      return this.translate('language.dutch.full');
    }
    return code.toUpperCase();
  }

  private interpolate(template: string, params?: TranslationParams): string {
    if (!params) {
      return template;
    }

    return Object.keys(params).reduce((acc, key) => {
      const value = params[key];
      return acc.replace(new RegExp(`{{\\s*${key}\\s*}}`, 'g'), String(value));
    }, template);
  }

  private resolveInitialLanguage(): SupportedLanguage {
    const urlLanguage = this.getLanguageFromPath();
    if (urlLanguage) {
      return urlLanguage;
    }

    const stored = this.getStoredLanguage();
    if (stored) {
      return stored;
    }

    if (typeof navigator !== 'undefined') {
      const candidate = navigator.languages?.[0] ?? navigator.language;
      if (candidate) {
        const normalized = candidate.slice(0, 2).toLowerCase();
        if (supportedLanguages.includes(normalized as SupportedLanguage)) {
          return normalized as SupportedLanguage;
        }
      }
    }

    return DEFAULT_LANGUAGE;
  }

  private getLanguageFromPath(): SupportedLanguage | null {
    const pathname = this.document?.location?.pathname;
    if (!pathname) {
      return null;
    }

    const firstSegment = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
    if (firstSegment && this.isSupportedLanguage(firstSegment)) {
      return firstSegment;
    }

    return null;
  }

  private getStoredLanguage(): SupportedLanguage | null {
    try {
      if (typeof localStorage === 'undefined') {
        return null;
      }
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && supportedLanguages.includes(stored as SupportedLanguage)) {
        return stored as SupportedLanguage;
      }
    } catch {
      return null;
    }
    return null;
  }

  private persistLanguage(language: SupportedLanguage): void {
    try {
      if (typeof localStorage === 'undefined') {
        return;
      }
      localStorage.setItem(STORAGE_KEY, language);
    } catch {
      // ignore persistence issues
    }
  }

  private applyDocumentLanguage(language: SupportedLanguage): void {
    if (!this.document?.documentElement) {
      return;
    }
    this.document.documentElement.setAttribute('lang', language);
  }

  private isTranslationKey(key: string): key is TranslationKey {
    return key in translations.en;
  }
}
